import api from './api';

export interface DashboardSnapshot {
  range: { from: string; to: string };
  today: {
    sales: number;
    platformRevenue: number;
    ordersCount: number;
    ordersSuccessful: number;
    ordersFailed: number;
    newUsers: number;
    newStores: number;
  };
  live: {
    ordersPending: number;
    problematicOrders: number;
    openDisputes: number;
    pendingWithdrawals: { count: number; amount: number };
    products: {
      active: number;
      outOfStock: number;
      lowStock: number;
      pendingApproval: number;
    };
  };
}

export interface DashboardTimeSeries {
  range: { from: string; to: string };
  series: Array<{
    date: string;
    sales: number;
    revenue: number;
    orders: number;
    newUsers: number;
  }>;
  ordersByStatus: Record<string, number>;
}

export type AlertSeverity = 'critical' | 'warning' | 'info';

export interface AlertItem {
  id: string;
  severity: AlertSeverity;
  title: string;
  description: string;
  count?: number;
  actionHref?: string;
}

export interface AlertsResponse {
  alerts: AlertItem[];
  counts: Record<AlertSeverity, number>;
}

export interface AttentionLists {
  problematicOrders: Array<{ id: string; orderNumber: string; totalAmount: number; createdAt: string; customer: string | null }>;
  pendingWithdrawals: Array<{ id: string; amount: number; requestDate: string; customer: string | null }>;
  openDisputes: Array<{ id: string; orderNumber: string; reason: string | null; status: string; createdAt: string; customer: string | null }>;
  lowStockProducts: Array<{ id: string; name: string; stock: number; store: string | null }>;
  pendingProducts: Array<{ id: string; name: string; createdAt: string; store: string | null }>;
}

function buildQuery(params?: { from?: string; to?: string }): string {
  const query: string[] = [];
  if (params?.from) query.push(`from=${encodeURIComponent(params.from)}`);
  if (params?.to) query.push(`to=${encodeURIComponent(params.to)}`);
  return query.length ? `?${query.join('&')}` : '';
}

// Dashboard widgets are best-effort: a 403 on any one of them must hide ONLY that
// widget, never redirect the user off the dashboard. `silentForbidden` tells the api
// layer to skip the global 403 → /403 redirect for these background calls.
const SILENT = { silentForbidden: true } as any;

const dashboardService = {
  /**
   * Snapshot for the overview dashboard.
   * Omit from/to to get "today" (start of today → now).
   */
  getSnapshot: async (params?: { from?: string; to?: string }) => {
    return api.get<DashboardSnapshot>(`/admin/dashboard/snapshot${buildQuery(params)}`, SILENT);
  },

  /** Daily time-series for the charts row. */
  getTimeSeries: async (params?: { from?: string; to?: string }) => {
    return api.get<DashboardTimeSeries>(`/admin/dashboard/timeseries${buildQuery(params)}`, SILENT);
  },

  /** Live "needs attention" lists (not range-scoped). */
  getAttention: async () => {
    return api.get<AttentionLists>('/admin/dashboard/attention', SILENT);
  },

  /** Risk / alert center evaluation. */
  getAlerts: async () => {
    return api.get<AlertsResponse>('/admin/dashboard/alerts', SILENT);
  },
};

export default dashboardService;
